'use client'
import HotelListItem from '@/app/components/HotelListItem'
import HotelListItemClick from '@/app/components/HotelListItemClick'
import HotelListItemSmall from '@/app/components/HotelListItemSmall'
import HotelsLoadingAnimation from '@/app/components/HotelsLoadingAnimation'
import RecommendationsEventHandler from '@/app/components/RecommendationsEventHandler'
import { SM_BREAKPOINT, TABLET_DOWN, UUID_NAMESPACE } from '@/app/constants'
import { useMapView } from '@/app/hooks/useMapView'
import { DestPagesProps, HotelItem, HotelsListResponse } from '@/app/types'
import {
	FetchNextPageOptions,
	InfiniteData,
	InfiniteQueryObserverResult,
} from '@tanstack/react-query'
import { useMediaQuery } from '@uidotdev/usehooks'
import classNames from 'classnames'
import { debounce } from 'lodash'
import { useTranslations } from 'next-intl'
import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { v5 as uuid } from 'uuid'
import HotelListPlaceholder from './HotelsListPlaceholder'
import Button from './common/Button'

export interface HotelsListProps {
	lang?: string
	data: HotelsListResponse
	pageParams?: DestPagesProps
	showLoadMore?: boolean
	isPageLoading?: boolean
	isLoadingHotels?: boolean
	singleHotelResponse?: HotelItem | null
	allData?: InfiniteData<HotelsListResponse>
	hasNextPage: boolean
	isFetchingNextPage: boolean
	shouldLoadMore: boolean
	totalPages: number
	isSingleHotelRequest?: boolean
	setHoveredItem: (id: string | number | null) => void
	isLandmark?: boolean
	isFallbackData?: boolean
	isSimilarData?: boolean
	isComplete?: boolean
	suppressLoadingAnimation?: boolean
	fetchNextPage: (
		options?: FetchNextPageOptions | undefined
	) => Promise<
		InfiniteQueryObserverResult<
			InfiniteData<HotelsListResponse, unknown>,
			Error
		>
	>
}

const HotelsList: FC<HotelsListProps> = memo((props) => {
	const {
		lang,
		data,
		singleHotelResponse,
		pageParams,
		showLoadMore,
		allData,
		hasNextPage,
		shouldLoadMore,
		isFetchingNextPage,
		totalPages,
		isSingleHotelRequest,
		isLoadingHotels,
		isLandmark,
		isComplete,
		isFallbackData,
		isSimilarData,
		isPageLoading,
		suppressLoadingAnimation,
		fetchNextPage,
		setHoveredItem,
	} = props
	const t = useTranslations('Keywords')
	const hotels = allData
		? allData.pages.flatMap((page) => page.hotels)
		: data.hotels
	const { isListView, isFullScreenMap } = useMapView();
	const [page, setPage] = useState(+(pageParams?.searchParams.page || 1))
	const hotelsListBodyRef = useRef<HTMLDivElement | null>(null)
	const tabletDown = useMediaQuery(TABLET_DOWN)
	const isMobile = useMediaQuery(SM_BREAKPOINT)
	const [country, region, city, hotel] = pageParams?.params.dest || [];

	const loadMoreHotels = useCallback(() => {
		if (
			isFetchingNextPage ||
			!hasNextPage ||
			!shouldLoadMore ||
			page >= totalPages
		)
			return

		fetchNextPage().finally(() => {
			setPage((prevPage) => prevPage + 1)
		})
	}, [
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		shouldLoadMore,
		page,
		totalPages,
	])

	const debouncedLoadMoreHotels = useMemo(() => debounce(loadMoreHotels, 300), [loadMoreHotels])

	useEffect(() => {
		return () => {
			debouncedLoadMoreHotels.cancel()
		}
	}, [debouncedLoadMoreHotels])

	useEffect(() => {
		const hotelsList = isListView ? document.documentElement : hotelsListBodyRef.current;
		const hotelsListBody = isListView ? document.documentElement : hotelsListBodyRef.current;

		if (!hotelsList || !hotelsListBody || !isListView) return;
		const handleScroll = () => {
			const scrollOffset = 750
			const shouldTriggerInfiniteScroll =
				shouldLoadMore &&
				page < Math.min(3, totalPages) &&
				window.innerHeight + hotelsList.scrollTop >=
				hotelsListBody.offsetHeight - scrollOffset &&
				hasNextPage &&
				!isFetchingNextPage &&
				page < totalPages

			if (shouldTriggerInfiniteScroll) {
				debouncedLoadMoreHotels()
			}
		}

		if (shouldLoadMore) {
			window.addEventListener('scroll', handleScroll)
			return () => {
				window.removeEventListener('scroll', handleScroll)
				debouncedLoadMoreHotels.cancel()
			}
		}
	}, [
		page,
		debouncedLoadMoreHotels,
		hasNextPage,
		isFetchingNextPage,
		shouldLoadMore,
		totalPages,
		isListView
	])

	const shouldShowLoadMoreButton =
		isListView &&
		showLoadMore &&
		shouldLoadMore &&
		hasNextPage &&
		!isFetchingNextPage &&
		page >= 3 &&
		page < totalPages

	const SingleHotelResponse = () => singleHotelResponse && (
		<>
			<RenderListArray listArray={[singleHotelResponse]} />
			<div className="font-bold text-sm mt-8 mb-4 max-md:mt-4 lg:text-lg text-text-900 leading-4">
				{t('hotelsSimilarTo')}
				<span className="mx-1">
					{decodeURIComponent(hotel)}
				</span>
			</div>
		</>
	)

	const RenderListArray = useMemo(() =>
		function RenderListArrayFn({ listArray }: { listArray: HotelItem[] }) {
			return listArray.map((item, index) =>
			(
				<div
					key={item.id}
					onMouseEnter={!isListView ? () => setHoveredItem(item.id) : undefined}
					onMouseLeave={!isListView ? () => setHoveredItem(null) : undefined}
					className={(isSingleHotelRequest && !isSimilarData) ? 'single-hotel-list-card' : ''}
				>
					{
						(isListView || tabletDown) && !isFullScreenMap ? (
							<HotelListItem
								item={item}
								index={index}
								lang={lang}
								searchParams={data.searchParams}
								isSingleHotel={isSingleHotelRequest}
								isLandmark={isLandmark}
							>
								<HotelListItemClick
									passthruLink={`/${lang}/passthru?uid=${uuid(
										item.link,
										UUID_NAMESPACE
									)}`}
									link={item.link}
									provider={item.provider}
									item={item}
									searchParams={data.searchParams}
									price={item.rawPrice}
								/>
							</HotelListItem>
						) : (
							<HotelListItemSmall
								key={item.id}
								item={item}
								index={index}
								lang={lang}
								searchParams={data.searchParams}
								isSingleHotel={isSingleHotelRequest}
								isLandmark={isLandmark}
							>
								<HotelListItemClick
									passthruLink={`/${lang}/passthru?uid=${uuid(
										item.link,
										UUID_NAMESPACE
									)}`}
									link={item.link}
									provider={item.provider}
									item={item}
									searchParams={data.searchParams}
									price={item.rawPrice}
								/>
							</HotelListItemSmall>
						)
					}
				</div>
			))
		}, [isListView, isSingleHotelRequest, tabletDown, isFullScreenMap, isLoadingHotels])


	const RenderListContent = useMemo(() => function RenderListContentFn() {
		return (
			<div className="hotels-list-body" ref={hotelsListBodyRef}>
				<RecommendationsEventHandler hotels={hotels} complete={data.complete} />
				<SingleHotelResponse />
				<RenderListArray listArray={hotels} />
				{shouldShowLoadMoreButton ? (
					<Button
						name="loadmore"
						textColor="tertiary-950"
						fontSize="xl"
						bgColor="tertiary-100"
						onClick={loadMoreHotels}
						bold
						className="!rounded-full w-full h-14 mt-8 hover:text-tertiary-hover hover:bg-tertiary-100/80"
					>
						{t('showMoreResults')}
					</Button>
				) : null}
				{isFetchingNextPage ? <HotelListPlaceholder isListView={isListView} showCount={false} /> : null}
			</div>
		)
	}, [shouldShowLoadMoreButton, isFetchingNextPage, isListView, hotels.length, data.complete, isFullScreenMap, isLoadingHotels])

	return (
		<div>
			{(isFallbackData || isSimilarData || (!isPageLoading && !isComplete && !isLoadingHotels))
				&& !suppressLoadingAnimation ? (
				<div
					className={classNames(
						'overflow-hidden transition-all h-0 delay-150 duration-300',
						{
							'h-[110px] mt-6': !isComplete,
							'mb-6': !isPageLoading
						}
					)}
				>
					<HotelsLoadingAnimation />
				</div>
			) : null}
			{isPageLoading || isLoadingHotels
				? <HotelListPlaceholder showCount={false} isListView={isListView} />
				: <RenderListContent />}
		</div>
	)
})
HotelsList.displayName = 'HotelsList';

export default HotelsList
